<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import type { BglFormSchemaFnT } from '@bagelink/vue'
import { bglForm } from '@/api/form'
import BotFooter from '@/components/BotFooter.vue'

import TopNav from '@/components/TopNav.vue'
import { offices } from '@/data/offices'
import { useLangTools, $t } from '@/lang'
import { BagelForm, bagelFormUtils, Card, Modal } from '@bagelink/vue'
import { useGtm } from '@gtm-support/vue-gtm'
import { useRouter } from 'vue-router'

const gtm = useGtm()

const { $tdb } = useLangTools(useRouter())

let scheduleMeeting = _ref('')
const showScheduleMeeting = _computed({
	get: () => !!scheduleMeeting.value,
	set: (val) => {
		if (!val) scheduleMeeting.value = ''
	}
})

const { frmRow, txtField, telField } = bagelFormUtils
const contactUsSchema: BglFormSchemaFnT = () => [
	frmRow(
		txtField('first_name', '', { placeholder: $t('general.name'), required: true }),
		txtField('last_name', '', { placeholder: $t('general.surname'), required: true }),
	),
	txtField('email', '', { placeholder: $t('general.email'), required: true }),
	telField('phone', '', { placeholder: $t('general.phone'), required: true }),
	txtField('comment', '', { placeholder: $t('general.message'), required: true, multiline: true }),
]

async function submitForm(formData: Record<string, any>) {
	try {
		await bglForm.postByFormName('Contact Form', formData)
		gtm?.trackEvent({
			event: 'form_submit',
			form_type: 'contact_form_submit',
			email: formData.email,
			phone: formData.phone,
		})
	} catch (err) {
		console.log(err)
	}
}
</script>

<template>
	<div>
		<div class="act_oval" />
		<TopNav />
		<Modal v-model:visible="showScheduleMeeting" width="900">
			<iframe title="scheduleMeeting" :src="scheduleMeeting" height="890px" width="100%" />
		</Modal>
		<div class="page-wrap relative">
			<div class="act_w1170 side-title">
				<div class="frame">
					<h1 class="playfair act_txt60 act_no-marg balance">
						{{ $t('contactUs.contactUs') }}
					</h1>
					<p class="act_pt-1 balance">
						{{ $t('contactUs.weAlwaysLove') }}
					</p>
				</div>
				<div class="white-box boxes-wrap">
					<div class="white-box act_center">
						<h3 class="playfair act_no-marg act_txt30 act_pb-1">
							{{ $t('contactUs.phoneEmailWhatsApp') }}
						</h3>
						<div class="contact-location">
							<div v-for="office in offices" :key="office._id">
								<div class="act_flex act_gap-05 align-center act_pb-05">
									<img
										width="27" height="20" class="contact-flag"
										:src="`/img/countries/${office.country}.svg`" :alt="office.country"
									>
									<p class="act_txt24">
										{{ office.country }}
										<!-- {{ $tdb(office, "name") }} -->
									</p>
								</div>
								<div v-if="office.email" class="act_flex act_gap-05">
									<img width="15" height="30" src="@/assets/contact-mail.svg" alt="mail">
									<a class="decoration-none" :href="`mailto:${office.email}`">{{ office.email }}</a>
								</div>
								<div v-if="office.phone" class="act_flex act_gap-05">
									<img width="13" height="30" src="@/assets/contact-phone.svg" alt="phone">
									<a class="decoration-none" :href="`tel:${office.phone}`">{{ office.phone }}</a>
								</div>
								<div v-if="office.whatsapp" class="act_flex act_gap-05">
									<img width="15" height="30" src="@/assets/contact-whatsapp.svg" alt="whatsapp">
									<a class="decoration-none" :href="office.whatsapp" target="_blank">Whatsapp</a>
								</div>
								<div v-if="office.google" class="act_flex act_gap-05">
									<img width="13" height="33" src="@/assets/Google_Maps.svg" alt="Google">
									<a class="decoration-none act_start" :href="office.google" target="_blank">
										{{ $tdb(office, 'address') }}</a>
								</div>
								<div v-if="office.meetingURL" class="act_flex act_gap-05">
									<button class="thin-btn" @click="scheduleMeeting = office.meetingURL">
										{{ $t('contactUs.bookAMeeting') }}
									</button>
								</div>
							</div>
						</div>
					</div>
					<Card class="mt-3 p-3 txt-center m_p-2">
						<h3 class="playfair act_no-marg act_txt30 act_pb-2">
							{{ $t('contactUs.writeUsAMessage') }}
						</h3>
						<BagelForm :schema="contactUsSchema" @submit="submitForm">
							<template #submit>
								<input type="submit" class="act_btn mobile-full" :value="$t('general.send')">
							</template>
							<template #error>
								<p class="form-fail">
									{{ $t('contactUs.error') }}
								</p>
							</template>
							<template #success>
								<p class="playfair act_no-marg act_txt30 act_pb-1">
									{{ $t('contactUs.thankYou') }}
								</p>
								<p>
									{{ $t('contactUs.touch') }}
								</p>
							</template>
						</BagelForm>
					</Card>
				</div>
			</div>
			<img class="bot-img" src="@/assets/contact-bg.webp" alt="watch">
		</div>
		<BotFooter />
	</div>
</template>
